/**
 * Bridge that lets Ahmed's vanilla-JS pilot modules submit leads through
 * our existing Apollo backend (submitPilotLead mutation -> pilot_leads
 * Firestore collection -> founder email via SendGrid).
 *
 * Ahmed's modules call window.__mp.submitLead(...) — that's set up by
 * `installPilotBridge()` which is invoked once on /pilot mount.
 */

import {ApolloClient} from '@apollo/client';
import {SUBMIT_PILOT_LEAD} from '../../graphql/pilotMutations';
import {getVisitorId, trackEvent} from '../pilotTracking';

export type PilotLeadKind =
  | 'PILOT_APPLICATION'
  | 'EXEC_WALKTHROUGH'
  | 'FOUNDER_CHAT'
  | 'ROI_EMAIL'
  | 'OPS_SIM_RESULT';

export interface PilotLeadInput {
  kind: PilotLeadKind;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  role?: string;
  linkedin?: string;
  ops?: string[];
  opsOther?: string;
  goals?: string;
  stack?: string;
  notes?: string;
  roiPayload?: Record<string, unknown>;
  simPayload?: Record<string, unknown>;
}

declare global {
  interface Window {
    __mp?: {
      submitLead?: (input: PilotLeadInput) => Promise<{ok: boolean; leadId?: string}>;
      track?: (name: string, payload?: Record<string, unknown>) => void;
    };
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function installPilotBridge(client: ApolloClient<unknown>): void {
  if (typeof window === 'undefined') return;

  window.__mp = {
    ...(window.__mp || {}),
    submitLead: async (input: PilotLeadInput) => {
      const email = (input.email || '').trim();
      if (!EMAIL_RE.test(email)) {
        return {ok: false};
      }
      try {
        const result = await client.mutate({
          mutation: SUBMIT_PILOT_LEAD,
          variables: {
            input: {
              kind: input.kind,
              email,
              firstName: input.firstName ?? undefined,
              lastName: input.lastName ?? undefined,
              company: input.company ?? undefined,
              role: input.role ?? undefined,
              linkedin: input.linkedin ?? undefined,
              ops: input.ops ?? undefined,
              opsOther: input.opsOther ?? undefined,
              goals: input.goals ?? undefined,
              stack: input.stack ?? undefined,
              notes: input.notes ?? undefined,
              visitorId: getVisitorId(),
              roiPayload: input.roiPayload,
              simPayload: input.simPayload,
            },
          },
        });
        const leadId = (result.data as {submitPilotLead?: {leadId?: string}})
          ?.submitPilotLead?.leadId;
        return {ok: true, leadId};
      } catch (err) {
        console.error('[pilot leadsBridge] submitLead failed:', err);
        return {ok: false};
      }
    },
    track: (name: string, payload?: Record<string, unknown>) => {
      trackEvent(name, payload);
    },
  };
}

export function uninstallPilotBridge(): void {
  if (typeof window === 'undefined') return;
  if (window.__mp) {
    delete window.__mp.submitLead;
    delete window.__mp.track;
  }
}
